import { findPath } from '@libs/NwUtils';

import { getPublishedBookItemKey } from '@components/MediaDrawer/publishedBooks.utils';

const compareLabels = (left = '', right = '') =>
    left.localeCompare(right, undefined, {
        sensitivity: 'base',
    });

const normalizeGroupKey = (value = '') => value.toString().trim().toLowerCase();

const getWebsiteName = (division) => {
    if (division?.WebsiteName) {
        return division.WebsiteName;
    }

    return division?.WebsiteCode?.toUpperCase() || '';
};

const getDivisionName = (division, websiteData = []) => {
    if (!division?.DivisionCode) {
        return '';
    }

    if (!websiteData.length) {
        return division.DivisionCode;
    }

    return findPath(websiteData, division.WebsiteCode, division.DivisionCode, '', true) || division.DivisionCode;
};

export const getCompactDivisionLabel = (divisionName = '', divisionCode = '') => {
    const source = divisionName || divisionCode || '';
    const compactName = source.includes('>') ? source.split('>').pop().trim() : source.trim();

    return compactName.toLowerCase();
};

export function getPlaylistPublicationKeys(playlists = []) {
    const publicationKeys = [];

    playlists.forEach((playlist) => {
        if (!playlist?.Divisions?.length) {
            return;
        }

        playlist.Divisions.forEach((division) => {
            publicationKeys.push(
                getPublishedBookItemKey({
                    Playlist: playlist,
                    WebsiteCode: division?.WebsiteCode,
                    DivisionCode: division?.DivisionCode,
                })
            );
        });
    });

    return publicationKeys;
}

// Builds a website -> division -> published books tree from the raw playlist.Divisions payload.
export function buildModelPublicationsSummary(playlists = [], websiteData = [], excludedKeys = new Set()) {
    const websitesMap = new Map();

    playlists.forEach((playlist) => {
        if (!playlist?.Divisions?.length) {
            return;
        }

        playlist.Divisions.forEach((division) => {
            const publication = {
                key: getPublishedBookItemKey({
                    Playlist: playlist,
                    WebsiteCode: division?.WebsiteCode,
                    DivisionCode: division?.DivisionCode,
                }),
                Playlist: playlist,
                Role: division?.Role || '',
                WebsiteCode: division?.WebsiteCode || '',
                WebsiteName: getWebsiteName(division),
                DivisionCode: division?.DivisionCode || '',
                DivisionName: getDivisionName(division, websiteData),
            };

            if (excludedKeys.has(publication.key)) {
                return;
            }

            const websiteKey = normalizeGroupKey(publication.WebsiteCode || publication.WebsiteName);
            if (!websitesMap.has(websiteKey)) {
                websitesMap.set(websiteKey, {
                    key: websiteKey,
                    websiteCode: publication.WebsiteCode,
                    websiteName: publication.WebsiteName,
                    divisionsMap: new Map(),
                    playlistIDs: new Set(),
                });
            }

            const websiteNode = websitesMap.get(websiteKey);
            if (!websiteNode.websiteName && publication.WebsiteName) {
                websiteNode.websiteName = publication.WebsiteName;
            }
            if (!websiteNode.websiteCode && publication.WebsiteCode) {
                websiteNode.websiteCode = publication.WebsiteCode;
            }
            websiteNode.playlistIDs.add(String(playlist.ID));

            const divisionKey = `${websiteKey}-${normalizeGroupKey(publication.DivisionCode)}`;
            if (!websiteNode.divisionsMap.has(divisionKey)) {
                websiteNode.divisionsMap.set(divisionKey, {
                    key: divisionKey,
                    divisionCode: publication.DivisionCode,
                    divisionName: publication.DivisionName,
                    booksMap: new Map(),
                    playlistIDs: new Set(),
                });
            }

            const divisionNode = websiteNode.divisionsMap.get(divisionKey);
            if (!divisionNode.divisionName && publication.DivisionName) {
                divisionNode.divisionName = publication.DivisionName;
            }
            if (!divisionNode.divisionCode && publication.DivisionCode) {
                divisionNode.divisionCode = publication.DivisionCode;
            }
            divisionNode.playlistIDs.add(String(playlist.ID));

            if (!divisionNode.booksMap.has(publication.key)) {
                divisionNode.booksMap.set(publication.key, publication);
            }
        });
    });

    return Array.from(websitesMap.values())
        .map((websiteNode) => ({
            key: websiteNode.key,
            websiteCode: websiteNode.websiteCode,
            websiteName: websiteNode.websiteName,
            booksCount: websiteNode.playlistIDs.size,
            divisionsCount: websiteNode.divisionsMap.size,
            divisions: Array.from(websiteNode.divisionsMap.values())
                .map((divisionNode) => ({
                    key: divisionNode.key,
                    divisionCode: divisionNode.divisionCode,
                    divisionName: divisionNode.divisionName,
                    booksCount: divisionNode.playlistIDs.size,
                    books: Array.from(divisionNode.booksMap.values()).sort((left, right) =>
                        compareLabels(left.Playlist?.Name || '', right.Playlist?.Name || '')
                    ),
                }))
                .sort((left, right) =>
                    compareLabels(
                        left.divisionName || left.divisionCode || '',
                        right.divisionName || right.divisionCode || ''
                    )
                ),
        }))
        .sort((left, right) => compareLabels(left.websiteName || left.websiteCode || '', right.websiteName || right.websiteCode || ''));
}

export function getWebsiteDivisionSummary(website) {
    const divisionLabels = Array.from(
        new Set(
            website.divisions
                .map((division) => getCompactDivisionLabel(division.divisionName, division.divisionCode))
                .filter(Boolean)
        )
    ).sort(compareLabels);

    return {
        divisionLabels,
        divisionsText: divisionLabels.join(', '),
    };
}

export function buildModelPublicationsSummaryContent(publicationsSummary = [], configuredWebsitesCount = 0) {
    const publishedWebsitesCount = publicationsSummary.length;
    const hasSingleConfiguredWebsite = configuredWebsitesCount === 1;

    if (hasSingleConfiguredWebsite) {
        const firstWebsite = publicationsSummary[0];
        const websiteSummary = firstWebsite ? getWebsiteDivisionSummary(firstWebsite) : { divisionsText: '' };

        return {
            title: 'PUBLISHED IN WEBSITE',
            lines: firstWebsite
                ? [
                      {
                          websiteName: '',
                          detailsText: websiteSummary.divisionsText || '-',
                          counterText: `${firstWebsite.booksCount} books`,
                      },
                  ]
                : [{ websiteName: '', detailsText: 'not published', counterText: '' }],
        };
    }

    return {
        title: `PUBLISHED IN ${publishedWebsitesCount} WEBSITE${publishedWebsitesCount === 1 ? '' : 'S'}`,
        lines: publishedWebsitesCount
            ? publicationsSummary.map((website) => {
                  const websiteSummary = getWebsiteDivisionSummary(website);

                  return {
                      websiteName: website.websiteName,
                      detailsText: websiteSummary.divisionsText || '-',
                      counterText: `${website.booksCount} books`,
                  };
              })
            : [{ websiteName: '', detailsText: 'not published', counterText: '' }],
    };
}
